import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Box, ButtonGroup, Divider, Typography, Skeleton } from "@mui/material";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { UsersDoughnutChart } from "./UsersDoughnutChart";
import { ByDayLineChart } from "./ByDayLineChart";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useGetAuditsQuery, useGetContentItemQuery, useGetInstanceSettingsQuery } from "../../../../../../../../shell/services/instance";
import { UsersBarChart } from "./UsersBarChart";
import { useParams as useQueryParams } from "../../../../../../../../shell/hooks/useParams";
import { useHistory, useParams } from "react-router-dom";
import { useGetAnalyticsPropertyDataByQueryQuery } from "../../../../../../../../shell/services/analytics";
import { convertSecondsToMinutesAndSeconds, findValuesForDimensions, generateReportRequests, getDateRangeAndLabelsFromParams } from "../../utils";
import { Metric } from "../../components/Metric";
import { useGetAnalyticsPropertiesQuery } from "../../../../../../../../shell/services/analytics";
import instanceZUID from "../../../../../../../../utility/instanceZUID";
import { NotFound } from "../../../../../../../../shell/components/NotFound";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import { CompareDialog } from "../../components/CompareDialog";
import { AnalyticsDateFilter } from "../../components/AnalyticsDateFilter";
import { ContentItem } from "../../../../../../../../shell/services/types";
import { AnalyticsPropertySelector } from "../../components/AnalyticsPropertySelector";
type Props = {
  item: ContentItem;
  loading: boolean;
};
export const SinglePageAnalytics = ({
  item,
  loading
}: Props) => {
  const {
    t
  } = useTranslation();
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [params, setParams] = useQueryParams();
  const compareItemZUID = params.get("compare");
  const {
    data: compareItem
  } = useGetContentItemQuery(compareItemZUID, {
    skip: !compareItemZUID
  });
  const {
    modelZUID,
    itemZUID
  } = useParams<{
    modelZUID: string;
    itemZUID: string;
  }>();
  const history = useHistory();
  const {
    data
  } = useGetAnalyticsPropertiesQuery();
  const {
    data: instanceSettings,
    isFetching: instanceSettingsFetching
  } = useGetInstanceSettingsQuery();
  const propertyId = instanceSettings?.find(setting => setting.key === "google_property_id")?.value;
  const propertyData = data?.properties?.find((property: any) => property.name === propertyId);
  const [startDate, endDate, dateRange0Label, dateRange1Label] = getDateRangeAndLabelsFromParams(params);
  const {
    data: ga4Data,
    isFetching,
    isError,
    refetch
  } = useGetAnalyticsPropertyDataByQueryQuery(generateReportRequests(propertyId, item.web.path, startDate, endDate), {
    skip: !startDate || !endDate || !propertyId
  });
  const [metricsReport, totalUsersReport, usersBySourceReport, usersByCountryReport, usersByDayReport] = ga4Data?.reports || [];
  const {
    data: compareGa4Data,
    isFetching: isFetchingCompare
  } = useGetAnalyticsPropertyDataByQueryQuery(generateReportRequests(propertyId, compareItem?.web?.path, startDate, endDate, false), {
    skip: !startDate || !endDate || !propertyId || !compareItem?.web?.path
  });
  const [comparedMetricsReport, comparedTotalUsersReport, comparedUsersBySourceReport, comparedUsersByCountryReport, comparedUsersByDayReport] = compareGa4Data?.reports || [];
  const {
    data: auditData
  } = useGetAuditsQuery({
    // Start of content item version support in audit
    start_date: "05/25/2023",
    affectedZUID: itemZUID
  });
  const isLoading = isFetching || instanceSettingsFetching || isFetchingCompare || loading;
  const averageEngagementTime = useMemo(() => {
    // TODO: calculate compared avg engagement time
    // Get all user count
    const dr0New = +(findValuesForDimensions(totalUsersReport?.rows, ["date_range_0", "new"], 0)[0] ?? 0);
    const dr0Returning = +(findValuesForDimensions(totalUsersReport?.rows, ["date_range_0", "returning"], 0)[0] ?? 0);
    const dr1New = +(findValuesForDimensions(totalUsersReport?.rows, ["date_range_1", "new"], 0)[0] ?? 0);
    const dr1Returning = +(findValuesForDimensions(totalUsersReport?.rows, ["date_range_1", "returning"], 0)[0] ?? 0);
    const comparedNew = +(findValuesForDimensions(comparedTotalUsersReport?.rows, ["new"], 0)[0] ?? 0);
    const comparedReturning = +(findValuesForDimensions(comparedTotalUsersReport?.rows, ["returning"], 0)[0] ?? 0);
    const dr0TotalUsers = dr0New + dr0Returning;
    const dr1TotalUsers = dr1New + dr1Returning;
    const comparedTotalUsers = comparedNew + comparedReturning;

    // Get total engagement time
    const dr0EngagementTime = +(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 4)[0] ?? 0);
    const dr1EngagementTime = +(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 4)[0] ?? 0);
    const comparedEngagementTime = +(comparedMetricsReport?.rows?.[0]?.metricValues?.[4]?.value ?? 0);

    // Calculate avg engagement time
    const dr0AvgEngagementTime = dr0TotalUsers !== 0 ? dr0EngagementTime / dr0TotalUsers : 0;
    const dr1AvgEngagementTime = dr1TotalUsers !== 0 ? dr1EngagementTime / dr1TotalUsers : 0;
    const comparedAvgEngagementTime = comparedTotalUsers !== 0 ? comparedEngagementTime / comparedTotalUsers : 0;
    return [dr0AvgEngagementTime, dr1AvgEngagementTime, comparedAvgEngagementTime];
  }, [metricsReport, totalUsersReport, comparedMetricsReport, comparedTotalUsersReport]);
  if (isError) {
    return <NotFound title={t("content.analyticsUnableToLoadTitle")} message={t("content.analyticsUnableToLoadMessage")} button={<>
            <Button startIcon={<SupportAgentRoundedIcon color="action" />} variant="contained" color="inherit" sx={{
        mr: 2
      }} onClick={() => window.open(`https://www.zesty.io/instances/${instanceZUID}/support/`)}>
              {t("content.analyticsContactSupport")}
            </Button>
            <Button startIcon={<RefreshRoundedIcon />} variant="contained" onClick={() => refetch()}>
              {t("content.analyticsTryAgain")}
            </Button>
          </>} />;
  }
  return <>
      <Box height="100%" data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Box display="flex" justifyContent="space-between" gap={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Box display="flex" gap={1.5} data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <AnalyticsDateFilter data-sentry-element="AnalyticsDateFilter" data-sentry-source-file="index.tsx" />
            {isLoading ? <Skeleton variant="rectangular" width="137px" height="28px" /> : compareItemZUID ? <ButtonGroup variant="contained" sx={{
            height: "28px",
            maxWidth: 460
          }}>
                <Button size="small" startIcon={<CompareArrowsRoundedIcon sx={{
              width: "20px",
              height: "20px"
            }} />} onClick={() => setShowCompareDialog(true)}>
                  <Typography noWrap color="inherit" variant="body2">
                    {compareItem?.web?.metaTitle || compareItem?.web?.path || compareItemZUID}
                  </Typography>
                </Button>
                <Button size="small" onClick={() => setParams(null, "compare")}>
                  <CloseRoundedIcon fontSize="small" />
                </Button>
              </ButtonGroup> : <Button variant="outlined" size="small" color="inherit" startIcon={<CompareArrowsRoundedIcon color="action" />} sx={{
            height: "28px",
            bgcolor: "background.paper"
          }} onClick={() => setShowCompareDialog(true)}>
                {t("content.analyticsComparePage")}
              </Button>}
          </Box>
          <AnalyticsPropertySelector path={item.web.path} data-sentry-element="AnalyticsPropertySelector" data-sentry-source-file="index.tsx" />
        </Box>
        <Box display="flex" justifyContent={"space-between"} alignItems="center" borderRadius={"8px"} gap={2} p={2} mt={2.5} border={theme => `1px solid ${theme.palette.border}`} bgcolor="background.paper" data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Metric loading={isLoading} title={t("content.analyticsMetricViews")} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 0)[0] ?? 0)} priorValue={compareItemZUID ? +comparedMetricsReport?.rows[0]?.metricValues?.[0]?.value || 0 : +(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 0)[0] ?? 0)} description={t("content.analyticsMetricViewsDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} title={t("content.analyticsMetricAvgEngagement")} formatter={convertSecondsToMinutesAndSeconds} value={averageEngagementTime[0]} priorValue={compareItemZUID ? averageEngagementTime[2] ?? 0 : averageEngagementTime[1]} description={t("content.analyticsMetricAvgEngagementDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} inverse title={t("content.analyticsMetricBounceRate")} formatter={(value: number) => `${Math.floor(value * 100)}%`} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 2)[0] ?? 0)} priorValue={compareItemZUID ? +comparedMetricsReport?.rows[0]?.metricValues?.[2]?.value || 0 : +(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 2)[0] ?? 0)} description={t("content.analyticsMetricBounceRateDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} title={t("content.analyticsMetricConversions")} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 3)[0] ?? 0)} priorValue={compareItemZUID ? +comparedMetricsReport?.rows[0]?.metricValues?.[3]?.value || 0 : +(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 3)[0] ?? 0)} description={t("content.analyticsMetricConversionsDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Box width="184px" height="100px" bgcolor="background.paper" data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <UsersDoughnutChart loading={isLoading} data={totalUsersReport} dateRange0Label={compareItemZUID ? item?.web?.metaTitle || item?.web?.path : dateRange0Label} dateRange1Label={compareItemZUID ? compareItem?.web?.metaTitle || item?.web?.path : dateRange1Label} compareData={comparedTotalUsersReport} shouldCompare={!!compareItemZUID} data-sentry-element="UsersDoughnutChart" data-sentry-source-file="index.tsx" />
          </Box>
        </Box>
        <Box display="flex" mt={2.5} gap={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Box width="40%" borderRadius={"8px"} p={2} border={theme => `1px solid ${theme.palette.border}`} bgcolor="background.paper" data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <UsersBarChart loading={isLoading} dateRange0Label={compareItemZUID ? item?.web?.metaTitle || item?.web?.path : dateRange0Label} dateRange1Label={compareItemZUID ? compareItem?.web?.metaTitle || item?.web?.path : dateRange1Label} usersBySourceReport={usersBySourceReport} usersByCountryReport={usersByCountryReport} comparedUsersBySourceReport={comparedUsersBySourceReport} comparedUsersByCountryReport={comparedUsersByCountryReport} shouldCompare={!!compareItemZUID} data-sentry-element="UsersBarChart" data-sentry-source-file="index.tsx" />
          </Box>

          <Box width="60%" borderRadius={"8px"} p={2} border={theme => `1px solid ${theme.palette.border}`} bgcolor="background.paper" data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <ByDayLineChart loading={isLoading} auditData={auditData} startDate={startDate} endDate={endDate} dateRange0Label={compareItemZUID ? item?.web?.metaTitle || item?.web?.path : dateRange0Label} dateRange1Label={compareItemZUID ? compareItem?.web?.metaTitle || item?.web?.path : dateRange1Label} data={usersByDayReport} compareData={comparedUsersByDayReport} shouldCompare={!!compareItemZUID} data-sentry-element="ByDayLineChart" data-sentry-source-file="index.tsx" />
          </Box>
        </Box>
      </Box>
      {showCompareDialog && <CompareDialog onClose={() => setShowCompareDialog(false)} />}
    </>;
};
export default SinglePageAnalytics;